import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {navigator} from 'lit-element-router';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';

import {ref, createRef} from 'lit/directives/ref.js';
import {msg} from '@lit/localize';

import store from '../store';
import {selectCurrentSection, selectFamilyConfig, selectSectionConfig, selectSpcAttributes} from '../selectors';
import next from '../../icons/next';
import previous from '../../icons/previous';
import {setMedia, setSection} from '../slices/statusSlice';
import {routeUrl} from '../util/routeUrl';
import {completionPercentage, isSectionSatisfied} from '../util/sectionTabInfo';

@customElement('sections-tabs')
export class SectionsTabs extends connect(store)(navigator(LitElement)) {

	static styles = css`
		:host {
			background-color: transparent;
			z-index: 1;
			display: flex;
			flex-direction: row;
			gap: 8px;
		}
		.tabscontainer {
			width: var(--features-column-width);
		}
		.title {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
		}
		.buttons {

		}
		.buttons button {
			background-color: transparent;
			border: none;
			color: var(--color-white);
			padding: 0;
			cursor: pointer;
		}
		.buttons button:disabled {
			color: var(--color-grey5);
			cursor: initial;
		}
		.label .family {
			font: var(--font-widget-big-label);
			color: var(--color-widget-text);
		}
		.label .section {
			font: var(--font-widget-label);
			color: var(--color-widget-text);
			transition: opacity 0.25s ease-in-out;
		}
		.label .section.hovered {
			opacity: 0.5;
		}

		.tabs {
			display: flex;
			justify-content: stretch;
			gap: 8px;
			box-sizing: border-box;
			z-index: 10;
		}
		.tab {
			flex-grow: 1;
		}

		@media (max-width: 768px) {
			.label {
				white-space: nowrap;
			}
		}

		.tabs::-webkit-scrollbar {
			display: none;
		}


		.icon {
			// margin-right: var(--very-small-padding);
			padding: 5px;
			line-height: 0;
			pointer-events: none;
			width: 11px;
			height: 11px;
			display: inline-block;
		}

		.icon svg * {
			display: block;
		}

    `;

	@property({type: String})
	currentSection = '';

	@property({type: String})
	hoveredSectionLabel = null;

	tabsRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData.productData;
		this.lang = state.status.lang;
		this.skipConfigurationEngine = state.status.skipConfigurationEngine;
		this.forceOverview = state.status.forceOverview;
		this.embed = state.status.embed;

		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.section = this.sectionConfig.find(s => s.id === this.currentSection);
		this.familyName = selectFamilyConfig(state).label?.[this.lang];
		this.spcAttributes = selectSpcAttributes(state);


		this.isInitialized = state.status.initialized;

		this.ensureTabInView(this.currentSectionIndex);

		this.lastSatisfiedIndex = 0;
		this.sectionConfig.some((section, index) => {
			this.lastSatisfiedIndex = index;
			return !isSectionSatisfied(section.id, state);
		});
		//console.log('lastSatisfiedIndex', this.lastSatisfiedIndex, this.isInitialized, this.currentSection);
	}


	ensureTabInView(index) {
		if (this.tabsRef.value) {
			const tab = this.tabsRef.value.children.item(index);
			const tabs = this.tabsRef.value;
			const offset = 32;
			tabs.scroll({left: tab.offsetLeft - offset, behavior: 'smooth'});
		}
	}

	previousSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
		const newSection = this.sectionConfig[newIndex].id;

		store.dispatch(setSection({currentSection: newSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}
	nextSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex < this.sectionConfig.length - 1 ? currentIndex + 1 : this.sectionConfig.length - 1;
		const newSection = this.sectionConfig[newIndex].id;

		if (currentIndex < this.sectionConfig.length - 1) {
			store.dispatch(setSection({currentSection: newSection}));
			store.dispatch(setMedia({media: 'sections'}));
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				// this.navigate(routeUrl({media: 'specsheet'}));
			}

		}
	}

	hoverSection(sectionId) {
		this.hoveredSectionLabel = sectionId !== null ? this.sectionConfig.find(s => s.id === sectionId).label?.[this.lang] : null;
	}

	render() {
		// if (!this.skipConfigurationEngine && (!this.features || this.features.length === 0)) return null;
		if (this.forceOverview || this.embed) return null;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const configurationDone = currentIndex >= this.lastSatisfiedIndex;
		return html`
			<div class="tabscontainer">
				<div class="title">
					<div class="label">
						<span class="family">${this.familyName}</span>
						<span class="section ${classMap({hovered: this.hoveredSectionLabel !== null})}">
							${this.hoveredSectionLabel === null ? this.section.label?.[this.lang] : this.hoveredSectionLabel}
						</span>
					</div>
					<div class="buttons">
						<button ?disabled=${currentIndex === 0} @click=${() => this.previousSection()}>
							<span class="icon">${previous}</span>
						</button>
						<button ?disabled=${currentIndex >= this.lastSatisfiedIndex || !this.isInitialized} @click=${() => this.nextSection()}>
							<span class="icon">${next}</span>
						</button>
					</div>
				</div>
				<nav class="tabs" ${ref(this.tabsRef)}>
					${this.sectionConfig
						.map((section, index) => html`
							${when(!section.notInNavigation && section.isVisible(this.state),
								() => html`
								<div class="tab">
									<sections-tab
										@mouseenter=${() => this.hoverSection(section.id)}
										@mouseleave=${() => this.hoverSection(null)}
										.disabled=${index > this.lastSatisfiedIndex || !this.isInitialized}
										key=${section.id}
										label=${section.label?.[this.lang]}
										.percentage=${completionPercentage(section.id, this.state)}
										.satisfied=${isSectionSatisfied(section.id, this.state)}
										.selected=${section.id === this.currentSection}
									>
									</sections-tab>
								</div>
							`)}
						`)}
				</nav>
			</div>


		`;
	}
}
